import { Grid, makeStyles, Theme, useMediaQuery, useTheme } from "@material-ui/core";
import React, { FunctionComponent, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { animated, useTransition } from "react-spring";
import { OnboardingContext } from "./context/onboarding-context";
import { useWindowHeight } from "../../hooks/use-window-height";
import { DigitalTwin } from "../../components/digital-twin/digital-twin";
import { APP_BAR_HEIGHT } from "../../components/topbar/top-bar";
import { OnboardingHeading } from "./onboarding-steps/components/onboarding-heading";
import { OnboardingNavButton } from "./onboarding-steps/components/onboarding-nav-button";
import { useTranslation } from "react-i18next";
import { OnboardingDetails } from "./onboarding-steps/onboarding-details";
import { OnboardingInfo } from "./onboarding-steps/onboarding-info";
import { ArrowRightIcon } from "../../assets/icons/arrows/arrow-right-icon";
import { ArrowLeftIcon } from "../../assets/icons/arrows/arrow-left-icon";
import { OnboardingSubheading } from "./onboarding-steps/components/onboarding-subheading";
import { BeatLoader } from "react-spinners";
import { PageIndicator } from "../../components/core/page-indicator";
import { ShopButton } from "../../components/shop-button/shop-button";
import { CardWithArrow } from "../../components/card-with-arrow/card-with-arrow";

const useStyles = makeStyles((theme: Theme) => ({
  mainSectionWrapper: {
    [theme.breakpoints.up("md")]: {
      marginTop: -APP_BAR_HEIGHT,
      paddingLeft: theme.spacing(4),
    },
  },
  mainSection: {
    position: "relative",
  },
  mainSectionItem: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    paddingTop: theme.spacing(5),
    [theme.breakpoints.up("md")]: {
      paddingTop: theme.spacing(10),
      maxWidth: 800,
    },
  },
  leftButton: {
    marginRight: theme.spacing(2),
  },
  cardWrapper: {
    width: "100%",
    margin: theme.spacing(2),
    [theme.breakpoints.up("md")]: {
      maxWidth: 800,
    },
  },
  cardContent: {
    minHeight: 550,
    [theme.breakpoints.up("md")]: {
      minHeight: 450,
    },
  },
  shopButtonContainer: {
    position: "absolute",
    width: "auto",
    zIndex: 1101,
    bottom: -90,
    right: 0,
    [theme.breakpoints.up("md")]: {
      position: "fixed",
      bottom: theme.spacing(4),
      right: theme.spacing(4),
    },
  },
}));

interface IOnboardingPageContentProps {}

enum OnboardingStep {
  INFO = "INFO",
  DETAILS = "DETAILS",
}

const onboardingTransitionComponents = {
  [OnboardingStep.INFO]: OnboardingInfo,
  [OnboardingStep.DETAILS]: OnboardingDetails,
};

export const OnboardingPageContent: FunctionComponent<IOnboardingPageContentProps> = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();

  const isMd = useMediaQuery(theme.breakpoints.up("md"));

  const [activeStep, setActiveStep] = useState<OnboardingStep>(OnboardingStep.INFO);
  const [done, setDone] = useState<boolean>(false);
  useEffect(() => setActiveStep(OnboardingStep.INFO), [setActiveStep]);

  const windowHeight = useWindowHeight();
  const canvasHeight = useMemo(() => (isMd ? windowHeight - APP_BAR_HEIGHT : 400), [windowHeight, isMd]);
  const { gender, allInputsValid, createUserInformation } = useContext(OnboardingContext);

  const transitions = useTransition(activeStep, (p) => p, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const keys = useMemo(() => Object.keys(onboardingTransitionComponents), []);

  const currentStepNumber = useMemo(() => keys.indexOf(activeStep), [keys, activeStep]);

  const previousKey = useMemo(() => {
    return currentStepNumber > 0 ? currentStepNumber - 1 : currentStepNumber;
  }, [currentStepNumber]);
  const previousStep = useMemo(() => keys[previousKey] as OnboardingStep, [keys, previousKey]);

  const nextKey = useMemo(() => {
    return currentStepNumber < keys.length - 1 ? currentStepNumber + 1 : currentStepNumber;
  }, [currentStepNumber, keys]);
  const nextStep = useMemo(() => keys[nextKey] as OnboardingStep, [keys, nextKey]);

  const isLastStep = useMemo(() => currentStepNumber === nextKey, [currentStepNumber, nextKey]);

  const nextButtonDisabled = useMemo(() => isLastStep && !allInputsValid, [isLastStep, allInputsValid]);
  const previousButtonDisabled = useMemo(() => currentStepNumber <= 0, [currentStepNumber]);

  const goToNextStep = useCallback(() => {
    if (isLastStep && allInputsValid) {
      createUserInformation();
      setDone(true);
    } else {
      setActiveStep(nextStep);
    }
  }, [isLastStep, nextStep, setActiveStep, createUserInformation, allInputsValid]);

  const setCurrentStep = useCallback(
    (pageNumber: number) => {
      setActiveStep(keys[pageNumber] as OnboardingStep);
    },
    [keys],
  );

  return (
    <Grid container>
      <Grid item md></Grid>
      <Grid item xs={12} md={"auto"}>
        <Grid container item justify={"center"}>
          <Grid container item justify={"center"} style={{ position: "relative" }}>
            <Grid item>
              <DigitalTwin height={canvasHeight} gender={gender} />
            </Grid>
            <Grid item className={classes.shopButtonContainer}>
              <ShopButton />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid item container xs={12} md={8} alignContent={"center"} className={classes.mainSectionWrapper}>
        <Grid item className={classes.cardWrapper}>
          <CardWithArrow showArrow arrowDirection={isMd ? "left" : "top"} arrowPosition={isMd ? "start" : "start"}>
            <Grid container direction="column" justify={"space-between"} className={classes.cardContent}>
              <Grid item>
                <OnboardingHeading>{t(`onboarding.introduction.header`)}</OnboardingHeading>
                <OnboardingSubheading>{t(`onboarding.introduction.description`)}</OnboardingSubheading>
              </Grid>
              {!done && (
                <Grid item style={{ flexGrow: 1 }}>
                  <Grid className={classes.mainSection}>
                    {transitions.map(({ item, props, key }) => {
                      const Component = onboardingTransitionComponents[item];
                      return (
                        <animated.div style={props} className={classes.mainSectionItem} key={key}>
                          <Component />
                        </animated.div>
                      );
                    })}
                  </Grid>
                </Grid>
              )}
              {done && (
                <Grid container justify="center" item xs alignItems="center" alignContent="center">
                  <BeatLoader color={theme.palette.primary.main} />
                </Grid>
              )}
              <Grid container item justify={"space-between"}>
                <Grid container item xs={6} justify={"flex-start"}>
                  <Grid item className={classes.leftButton}>
                    <OnboardingNavButton
                      onClick={() => {
                        setActiveStep(previousStep);
                      }}
                      disabled={previousButtonDisabled}
                    >
                      <ArrowLeftIcon width={24} />
                    </OnboardingNavButton>
                  </Grid>
                  <Grid item>
                    <OnboardingNavButton onClick={goToNextStep} disabled={nextButtonDisabled}>
                      <ArrowRightIcon width={24} />
                    </OnboardingNavButton>
                  </Grid>
                </Grid>
                <Grid container item xs={6} justify={"flex-end"} alignItems={"center"}>
                  <Grid item>
                    <PageIndicator
                      pages={keys.length}
                      activePage={currentStepNumber}
                      onClickPageIndicator={setCurrentStep}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </CardWithArrow>
        </Grid>
      </Grid>
    </Grid>
  );
};
